import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, StatusPill, cn, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Layout, Topbar } from '../components/Layout';
import { fmtDate } from '../lib/fmt';
import { InvoiceModal, PaymentModal } from '../components/InvoiceForms';

type Invoice = {
  id: string; invoice_number: string | null; kind: string; status: string; currency: string;
  subtotal: number; tax_total: number; tax_label: string | null; total: number; amount_paid: number; balance: number;
  issue_date: string | null; due_date: string | null; client_id: string | null; property_id: string | null;
  clients: { name: string } | null; properties: { name: string } | null;
};
type Line = { id: string; description: string; quantity: number; unit_price: number; line_total: number };
type Payment = { id: string; amount: number; method: string; paid_on: string; reference: string | null; reconciled: boolean };
const TONE: Record<string, 'open' | 'inprogress' | 'done' | 'overdue' | 'onhold'> = { draft: 'open', issued: 'inprogress', partial: 'onhold', paid: 'done', void: 'overdue' };

export function Invoices() {
  const { t, i18n } = useTranslation();
  const { id: orgId, timezone } = useOrg();
  const [rows, setRows] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState<string | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [newOpen, setNewOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    const r = await supabase.from('invoices')
      .select('id,invoice_number,kind,status,currency,subtotal,tax_total,tax_label,total,amount_paid,balance,issue_date,due_date,client_id,property_id,clients(name),properties(name)')
      .is('deleted_at', null).order('created_at', { ascending: false });
    const list = (r.data as unknown as Invoice[]) ?? [];
    setRows(list); setSel((cur) => cur ?? list[0]?.id ?? null); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!sel) { setLines([]); setPayments([]); return; }
    supabase.from('invoice_lines').select('id,description,quantity,unit_price,line_total').eq('invoice_id', sel).is('deleted_at', null).order('position').then((r) => setLines((r.data as Line[]) ?? []));
    supabase.from('payments').select('id,amount,method,paid_on,reference,reconciled').eq('invoice_id', sel).is('deleted_at', null).order('paid_on', { ascending: false }).then((r) => setPayments((r.data as Payment[]) ?? []));
  }, [sel]);

  const current = rows.find((i) => i.id === sel) ?? null;

  async function issue(id: string) { const { error } = await supabase.rpc('issue_invoice', { p_invoice: id }); if (!error) load(); else setMsg(error.message); }
  async function credit(id: string) { const { data, error } = await supabase.rpc('create_credit_note', { p_invoice: id }); if (!error) { setSel(data as string); load(); } else setMsg(error.message); }
  async function toggleRecon(p: Payment) { await supabase.from('payments').update({ reconciled: !p.reconciled }).eq('id', p.id); setSel((s) => s); if (sel) supabase.from('payments').select('id,amount,method,paid_on,reference,reconciled').eq('invoice_id', sel).is('deleted_at', null).order('paid_on', { ascending: false }).then((r) => setPayments((r.data as Payment[]) ?? [])); }
  async function payOnline(id: string) { const { error } = await supabase.rpc('create_payment_intent', { p_invoice: id }); setMsg(error ? t('inv.gatewayStub') : ''); }

  return (
    <Layout header={<Topbar actions={<Button onClick={() => setNewOpen(true)}>+ {t('inv.new')}</Button>} />}>
      <div className="px-7 pt-6"><h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('nav.invoices')}</h1></div>
      {msg && <div className="mx-7 mt-3 rounded-md bg-[#FFF7E6] px-3 py-2 text-sm text-[#8A5A00]">{msg} <button className="ml-2 underline" onClick={() => setMsg('')}>✕</button></div>}
      <div className="flex flex-wrap items-start gap-[18px] px-7 pb-7 pt-[18px]">
        <Card className="min-w-0 max-w-[460px] flex-1 overflow-hidden p-0" style={{ flexBasis: 380 }}>
          {loading ? <div className="p-6 text-sm text-muted">{t('common.loading')}</div>
            : rows.length === 0 ? <div className="p-6 text-sm text-muted">{t('common.noData')}</div>
            : rows.map((i) => (
              <button key={i.id} onClick={() => setSel(i.id)} className={cn('flex w-full flex-col gap-1.5 border-b border-divider p-4 text-left last:border-0', i.id === sel ? 'bg-[#F5F7FF] shadow-[inset_3px_0_0_var(--pv-accent)]' : 'hover:bg-bg')}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium"><span className="font-mono text-xs text-muted">{i.invoice_number ?? t('quote.st_draft')}</span>{i.kind === 'credit_note' ? ` · ${t('inv.creditNote')}` : ''}</span>
                  <span className="font-mono text-xs">{formatCurrency(i.total, i.currency, i18n.language)}</span>
                </div>
                <div className="flex items-center gap-2"><StatusPill tone={TONE[i.status] ?? 'open'}>{t('inv.st_' + i.status)}</StatusPill><span className="text-sm text-muted">{i.clients?.name ?? i.properties?.name ?? ''}</span></div>
              </button>
            ))}
        </Card>

        {current && (
          <Card className="min-w-0 flex-[2] p-6" style={{ flexBasis: 480 }}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <span className="font-mono text-xs text-muted">{current.invoice_number ?? t('quote.st_draft')}{current.kind === 'credit_note' ? ` · ${t('inv.creditNote')}` : ''}</span>
                <h2 className="text-[20px] font-semibold">{current.clients?.name ?? current.properties?.name ?? '—'}</h2>
                <span className="text-sm text-muted">{current.issue_date ? fmtDate(current.issue_date, i18n.language, timezone) : ''}{current.due_date ? ` → ${fmtDate(current.due_date, i18n.language, timezone)}` : ''}</span>
              </div>
              <StatusPill tone={TONE[current.status] ?? 'open'}>{t('inv.st_' + current.status)}</StatusPill>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              {current.status === 'draft' && <Button size="sm" variant="accent" onClick={() => issue(current.id)}>{t('inv.issue')}</Button>}
              {['issued', 'partial'].includes(current.status) && <Button size="sm" onClick={() => setPayOpen(true)}>{t('inv.recordPayment')}</Button>}
              {['issued', 'partial', 'paid'].includes(current.status) && current.kind === 'invoice' && <Button size="sm" variant="secondary" onClick={() => credit(current.id)}>{t('inv.creditNote')}</Button>}
              {['issued', 'partial'].includes(current.status) && <Button size="sm" variant="secondary" onClick={() => payOnline(current.id)}>{t('inv.payOnline')}</Button>}
              <Link to={`/invoices/${current.id}/print`} target="_blank"><Button size="sm" variant="secondary">{t('quote.print')}</Button></Link>
            </div>

            <table className="mt-5 w-full border-collapse text-sm">
              <tbody>
                {lines.map((l) => (
                  <tr key={l.id} className="border-b border-divider">
                    <td className="py-2.5 pr-2">{l.description}</td>
                    <td className="py-2.5 pr-2 text-right font-mono text-xs text-muted">{l.quantity} × {formatCurrency(l.unit_price, current.currency, i18n.language)}</td>
                    <td className="py-2.5 text-right font-mono">{formatCurrency(l.line_total, current.currency, i18n.language)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 flex flex-col items-end gap-0.5 text-sm">
              <span className="text-muted">{t('cost.total')} HT: <span className="font-mono text-fg">{formatCurrency(current.subtotal, current.currency, i18n.language)}</span></span>
              {current.tax_total !== 0 && <span className="text-muted">{current.tax_label ?? t('inv.tax')}: <span className="font-mono text-fg">{formatCurrency(current.tax_total, current.currency, i18n.language)}</span></span>}
              <span className="font-mono text-base font-semibold">{formatCurrency(current.total, current.currency, i18n.language)}</span>
              {current.amount_paid > 0 && <span className="text-muted">{t('inv.paid')}: <span className="font-mono text-st-done-fg">{formatCurrency(current.amount_paid, current.currency, i18n.language)}</span> · {t('inv.balance')}: <span className="font-mono">{formatCurrency(current.balance, current.currency, i18n.language)}</span></span>}
            </div>

            {payments.length > 0 && (
              <div className="mt-5 border-t border-divider pt-3">
                <h3 className="mb-2 text-[15px] font-semibold">{t('inv.payments')}</h3>
                {payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-3 border-b border-divider py-2 text-sm last:border-0">
                    <span>{fmtDate(p.paid_on, i18n.language, timezone)} · {t('inv.pm_' + p.method)}{p.reference ? ` · ${p.reference}` : ''}</span>
                    <div className="flex items-center gap-3">
                      <button onClick={() => toggleRecon(p)} className={cn('rounded-pill px-2 py-0.5 text-2xs', p.reconciled ? 'bg-st-done-bg text-st-done-fg' : 'bg-bg text-muted')}>{p.reconciled ? t('inv.reconciled') : t('inv.reconcile')}</button>
                      <span className="font-mono">{formatCurrency(p.amount, current.currency, i18n.language)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>
      <InvoiceModal open={newOpen} onClose={() => setNewOpen(false)} onSaved={(id) => { load(); if (id) setSel(id); }} />
      {current && <PaymentModal open={payOpen} onClose={() => setPayOpen(false)} onSaved={load} invoiceId={current.id} orgId={orgId} currency={current.currency} balance={current.balance} />}
    </Layout>
  );
}
